"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export function LawWebsiteBooking({ section }) {
  return (
    <section className="py-12 relative overflow-hidden bg-transparent w-full font-sans text-white relative z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">

        {/* Header Block */}
        <div className="text-center max-w-[1000px] mx-auto mb-16 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex justify-center"
          >
            {/* Brand Orange Calendar Icon (Shifted from Red) */}
            <Image
              src="/odoo/icons/red_calendar.svg"
              width={80}
              height={80}
              className="h-20 w-auto select-none pointer-events-none mb-2"
              alt=""
              style={{ filter: "hue-rotate(25deg) saturate(1.4)" }}
            />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight ${caveat.className}`}
          >
            Website ready for{" "}
            <span className="relative inline-block px-6 ml-1">
              <span className="relative z-10 text-white">appointment</span>
              {/* Slanted Brand Orange marker highlighter background */}
              <svg className="absolute inset-0 w-full h-[120%] -top-[10%] -z-10 scale-y-110 origin-center opacity-85" viewBox="0 0 100 100" preserveAspectRatio="none">
                <path
                  d="M 5 25 C 30 18, 70 12, 95 15 C 98 25, 96 45, 95 75 C 70 78, 30 82, 5 78 C 2 60, 3 40, 5 25 Z"
                  fill="#f1833b"
                />
              </svg>
            </span>{" "}
            booking
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-gray-300 leading-relaxed font-normal max-w-3xl mx-auto text-xl"
          >
            <strong className="text-white font-bold">Create a stunning website using Odoo's AI-based builder,</strong> 3+ million free photos, and drag-and-drop elements. No coding skills required.
          </motion.p>
        </div>

        {/* Dynamic Image & Text Layout Block */}
        <div className="grid lg:grid-cols-12 gap-12 items-start max-w-[1250px] mx-auto mt-12 relative">

          {/* Subtle backdrop glow in theme orange */}
          <div className="absolute inset-0 bg-[#f1833b]/5 rounded-full blur-[140px] pointer-events-none -z-10" />

          {/* Left Column: Template Mockup */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 space-y-6"
          >
            <Image
              src="/odoo/industries/law-firm/appointments-1.webp"
              width={500}
              height={400}
              className="w-full h-auto rounded-2xl border border-white/10 shadow-2xl object-contain hover:scale-[1.02] transition-transform duration-300"
              alt="Lawyers Directory Mockup"
            />
            <p className="text-gray-300 leading-relaxed font-normal text-xl">
              <strong className="text-white font-bold">Use page templates</strong> to create content such as “About us” page, contact forms, blog posts, and more.
            </p>
          </motion.div>

          {/* Right Column: Appointment Scheduler */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-6 lg:col-start-7 flex flex-col justify-between h-full space-y-6"
          >
            <p className="text-gray-300 leading-relaxed font-normal lg:pt-8 text-xl">
              <strong className="text-white font-bold">Showcase different appointment types</strong> on your website and allow clients to book consultations online. Everything is synchronized with your calendar for seamless planning.
            </p>
            <Image
              src="/odoo/industries/law-firm/appointments-2.webp"
              width={600}
              height={450}
              className="w-full h-auto rounded-2xl border border-white/10 shadow-2xl object-contain hover:scale-[1.02] transition-transform duration-300"
              alt="Appointment Booking Mockup"
            />
          </motion.div>

        </div>

        {/* Rotated loop arrow transition pointing downwards to next section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full flex justify-center mt-16 select-none pointer-events-none"
        >
          <Image
            src="/odoo/arrows/grey_arrow_dot_02.svg"
            className="brightness-0 invert opacity-45 transform rotate-[40deg]"
            width={140}
            height={140}
            style={{ height: "140px", width: "auto" }}
            alt=""
          />
        </motion.div>

      </div>
    </section>
  );
}

export function LawCaseManagement({ section }) {
  return (
    <section className="py-20 relative overflow-hidden bg-transparent w-full font-sans text-white relative z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16">

          {/* Left Column: Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="w-full lg:w-[50%] space-y-6"
          >
            <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight ${caveat.className}`}>
              <span className="relative inline-block mr-2">
                <span className="relative z-10 text-white">Centralized</span>
                {/* Wavy brand orange highlighter underline stroke */}
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#f1833b] overflow-visible opacity-90" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M 2 8 C 30 4, 70 3, 98 5 C 75 7, 35 9, 5 8" stroke="currentColor" strokeWidth="4.5" fill="none" strokeLinecap="round" />
                </svg>
              </span>{" "}
              case management
            </h2>

            <div className="space-y-4">
              <p className="text-xl text-gray-300 leading-relaxed">
                Once you sell your services, Odoo automatically creates projects and tasks for each case.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Everything is centralized in an all-in-one dashboard.</strong> Use tags, statuses, color-coding, and activities to never miss a follow-up.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                Your project's top bar contains all records related to a case: timesheets, sales orders, invoices, and more.
              </p>
            </div>
          </motion.div>

          {/* Right Column: Kanban Image Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="w-[90%] sm:w-[75%] md:w-[65%] lg:w-[42%] relative flex justify-center group"
          >
            {/* Backdrop circle glow matching the star theme */}
            <div className="absolute inset-0 bg-[#f1833b]/5 rounded-full blur-[120px] pointer-events-none -z-10" />

            {/* Light circular background backdrop matching Odoo's x_wd_shape_circle */}
            <div className="absolute top-[10%] bottom-[10%] left-[-10%] right-[-10%] bg-white/[0.02] rounded-full -z-10 pointer-events-none" />

            {/* Orange Corner Sparkle Doodle, placed perfectly on the top-right corner and pointing strictly outwards */}
            <div className="absolute -top-8 -right-8 w-20 h-20 pointer-events-none select-none z-20 opacity-100 group-hover:scale-110 transition-transform duration-300">
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-[#f1833b]">
                {/* Ray 1: pointing top-right */}
                <path d="M 30 70 C 45 55, 60 42, 85 34" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
                {/* Ray 2: pointing straight up */}
                <path d="M 40 65 C 44 48, 48 30, 52 12" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
                {/* Ray 3: pointing middle-right */}
                <path d="M 48 70 C 62 66, 78 62, 94 58" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
                {/* Ray 4: pointing bottom-right */}
                <path d="M 42 78 C 52 86, 64 94, 76 99" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
              </svg>
            </div>

            <Image
              src="/odoo/industries/law-firm/kanban.webp"
              width={500}
              height={450}
              className="w-full h-auto rounded-2xl border border-white/10 shadow-2xl object-contain hover:scale-[1.02] transition-transform duration-300"
              alt="Odoo Kanban Case Management"
            />
          </motion.div>

        </div>
      </div>
    </section>
  );
}

export function LawDocumentWorkspace({ section }) {
  return (
    <section className="py-20 relative overflow-hidden bg-transparent w-full font-sans text-white relative z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">

        {/* Title row */}
        <div className="max-w-[1000px] mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight ${caveat.className}`}
          >
            <span className="relative inline-block mr-2">
              <span className="relative z-10 text-white">Dedicated</span>
              {/* Brand Orange Highlighter Underline Stroke */}
              <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#f1833b] overflow-visible opacity-90" viewBox="0 0 100 10" preserveAspectRatio="none">
                <path d="M 2 8 C 30 4, 70 3, 98 5 C 75 7, 35 9, 5 8" stroke="currentColor" strokeWidth="4.5" fill="none" strokeLinecap="round" />
              </svg>
            </span>{" "}
            document workspace
          </motion.h2>
        </div>

        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16">

          {/* Left Column: Mockup Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="w-full lg:w-[55%] relative flex justify-center group"
          >
            {/* Ambient background glow */}
            <div className="absolute inset-0 bg-[#f1833b]/5 rounded-full blur-[120px] pointer-events-none -z-10" />

            <Image
              src="/odoo/industries/law-firm/documents.webp"
              width={700}
              height={500}
              className="w-full h-auto rounded-2xl border border-white/10 shadow-2xl object-contain hover:scale-[1.02] transition-transform duration-300"
              alt="Odoo Document Workspace"
            />
          </motion.div>

          {/* Right Column: Content & eSign Callout */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="w-full lg:w-[40%] space-y-8"
          >
            <div className="space-y-4">
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Each case gets its own document workspace,</strong> where you can easily store, organize, and manage documents in a secure way.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Odoo's eSign</strong> ensures that you can provide legally binding signatures online - all it takes is a click.
              </p>
            </div>

            {/* Persona Callout Banner */}
            <div className="flex items-center gap-4 pt-4">
              <Image
                src="/odoo/snippets/s_wd_persona/foto.webp"
                width={56}
                height={56}
                className="w-14 h-14 rounded-full border-2 border-white/20 shadow-md select-none pointer-events-none"
                alt=""
              />
              <div className="bg-white/[0.04] border border-white/10 rounded-full py-3 px-6 shadow-lg backdrop-blur-sm">
                <span className="text-gray-200 text-sm md:text-base font-normal">
                  Find out more about{" "}

                  eSign

                </span>
              </div>
            </div>

          </motion.div>

        </div>
      </div>
    </section>
  );
}

export function LawStreamlinedFinances({ section }) {
  const cols = [
    {
      text: (<><strong className="text-white font-bold">Invoice clients based on fixed rates, timesheets, or milestones.</strong> Accept upfront or down payments; Odoo adapts to your unique business needs.</>),
      image: "/odoo/industries/law-firm/milestones.webp",
      alt: "Milestones",
      arrow: { src: "/odoo/arrows/arrow_sm_02.svg", rotate: "rotate-[220deg] scale-x-[-1]", side: "right" }
    },
    {
      text: (<><strong className="text-white font-bold">A professional invoice</strong> is created and sent to the customer in a flash — no extra work needed.</>),
      image: "/odoo/industries/law-firm/invoice.webp",
      alt: "Invoice"
    },
    {
      text: (<><strong className="text-white font-bold">Odoo's integrated timesheets</strong> allow for easy time tracking from a project task or a dedicated app, on laptops or mobile devices — the choice is yours.</>),
      image: "/odoo/industries/law-firm/timesheets.webp",
      alt: "Timesheets",
      arrow: { src: "/odoo/arrows/arrow_sm_02.svg", rotate: "rotate-[270deg] scale-x-[-1]", side: "left" }
    }
  ];

  return (
    <section className="py-20 relative overflow-hidden bg-transparent w-full font-sans text-white relative z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap items-center justify-center gap-4 mb-16"
        >
          <Image
            src="/odoo/icons/blue_coin.svg"
            width={64}
            height={64}
            className="h-16 w-auto select-none pointer-events-none"
            style={{ filter: "hue-rotate(25deg) saturate(1.3)" }}
            alt=""
          />
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight ${caveat.className}`}>
            Streamlined finances
          </h2>
          <Image
            src="/odoo/icons/blue_coin-2.svg"
            width={64}
            height={64}
            className="h-16 w-auto select-none pointer-events-none"
            style={{ filter: "hue-rotate(25deg) saturate(1.3)" }}
            alt=""
          />
        </motion.div>

        {/* 3-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14 items-start">
          {cols.map((col, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className="flex flex-col gap-5"
            >
              <p className="text-gray-300 leading-relaxed text-xl">{col.text}</p>

              {col.arrow && (
                <div className={`hidden lg:flex ${col.arrow.side === "right" ? "justify-end" : "justify-start"}`}>
                  <Image
                    src={col.arrow.src}
                    width={36}
                    height={36}
                    className={`w-9 h-9 opacity-50 select-none pointer-events-none ${col.arrow.rotate}`}
                    alt=""
                    style={{ filter: "brightness(0) invert(1)" }}
                  />
                </div>
              )}

              <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-white/5 hover:scale-[1.02] transition-transform duration-300">
                <Image
                  src={col.image}
                  width={400}
                  height={300}
                  className="w-full h-auto object-contain"
                  alt={col.alt}
                />
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
